import { useState, useEffect, useRef, useCallback } from "react";
import { ref, push, set } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { database, storage } from "../constants/firebase";

export const useRecording = (chatType, chatId, currentUser, chatUser = null) => {
    const [recordingPopup, setRecordingPopup] = useState(false);
    const [mediaRecorder, setMediaRecorder] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [audioChunks, setAudioChunks] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    // Helper: base64 to Blob
    const base64ToBlob = useCallback((base64, type) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type });
    }, []);

    // Get storage path based on chat type
    const getStoragePath = useCallback((filename) => {
        switch (chatType) {
            case 'private':
                return `privateChats/${chatId}/${filename}`;
            case 'group':
                return `groupChats/${chatId}/${filename}`;
            case 'community':
                return `communityMessages/${chatId}/${filename}`;
            default:
                return null;
        }
    }, [chatType, chatId]);

    // Get database path based on chat type
    const getDatabasePath = useCallback(() => {
        switch (chatType) {
            case 'private':
                return `privateChats/${chatId}`;
            case 'group':
                return `groupChats/${chatId}`;
            case 'community':
                return `communityMessages/${chatId}`;
            default:
                return null;
        }
    }, [chatType, chatId]);

    // Handle native app messages
    useEffect(() => {
        const handleMessage = async (event) => {
            const data = JSON.parse(event.data);

            switch (data.type) {
                case "RECORDING_PERMISSION_GRANTED":
                    console.log("✅ Mic permission granted by app");
                    break;

                case "RECORDING_PERMISSION_DENIED":
                    alert("Microphone permission denied.");
                    break;

                case "RECORDING_STARTED":
                    console.log("🎙️ Recording started inside app");
                    setRecordingPopup(true);
                    break;

                case "RECORDING_STOPPED":
                    console.log("🛑 Recording stopped from native app:", data.filePath);
                    if (data.base64) {
                        let mime = data.mime || "audio/webm";
                        const blob = base64ToBlob(data.base64, mime);
                        await uploadAudio(blob);
                    }
                    setRecordingPopup(false);
                    break;

                case "RECORDING_CANCELED":
                    console.log("🎙️ Recording canceled from native app");
                    setRecordingPopup(false);
                    break;

                case "RECORDING_STOP_ERROR":
                case "RECORDING_PERMISSION_ERROR":
                    console.error("Recording error:", data.error);
                    alert("Error recording audio: " + data.error);
                    break;

                default:
                    break;
            }
        };

        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, [base64ToBlob]);

    // Start recording
    const startRecording = useCallback(async () => {
        // Check if we're in the app and on iOS
        if (window.isInApp && window.ReactNativeWebView && window.isIOS) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "START_RECORDING" }),
            );
        } else {
            // Fallback for web browser
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        channelCount: 1,
                        sampleRate: 44100,
                        bitrate: 128000,
                    },
                });

                const options = {
                    audioBitsPerSecond: 128000,
                    mimeType: MediaRecorder.isTypeSupported("audio/mp4")
                        ? "audio/mp4"
                        : MediaRecorder.isTypeSupported("audio/aac")
                            ? "audio/aac"
                            : "audio/webm",
                };

                const recorder = new MediaRecorder(stream, options);
                const chunks = [];

                recorder.ondataavailable = (e) => chunks.push(e.data);
                recorder.start();

                setMediaRecorder(recorder);
                setAudioChunks(chunks);
                setRecordingPopup(true);
                setIsRecording(true);
            } catch (err) {
                console.error("Recording error:", err);
                alert("Could not access microphone. Please check permissions.");
            }
        }
    }, []);

    // Stop recording
    const stopRecording = useCallback(async () => {
        if (window.isInApp && window.ReactNativeWebView && window.isIOS) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "STOP_RECORDING" }),
            );
        } else {
            if (mediaRecorder) {
                mediaRecorder.stop();

                const audioBlob = await new Promise((resolve) => {
                    mediaRecorder.onstop = () => {
                        const blob = new Blob(audioChunks, { type: "audio/mp4" });
                        resolve(blob);
                    };
                });

                await uploadAudio(audioBlob);
                cleanupRecording();
            }
        }
    }, [mediaRecorder, audioChunks]);

    // Cancel recording
    const cancelRecording = useCallback(() => {
        if (window.isInApp && window.ReactNativeWebView && window.isIOS) {
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "CANCEL_RECORDING" }),
            );
        } else {
            if (mediaRecorder) {
                mediaRecorder.stop();
                cleanupRecording();
            }
        }
    }, [mediaRecorder]);

    // Cleanup recording
    const cleanupRecording = useCallback(() => {
        if (mediaRecorder) {
            if (mediaRecorder.stream) {
                mediaRecorder.stream.getTracks().forEach((track) => track.stop());
            }
            setRecordingPopup(false);
            setMediaRecorder(null);
            setAudioChunks([]);
            setIsRecording(false);
        }
    }, [mediaRecorder]);

    // Upload audio
    const uploadAudio = useCallback(async (blob) => {
        if (!currentUser || !chatId) return;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const storage = getStorage();
            const extension = "webm"; // Use webm for all platforms
            const filename = `voice_${Date.now()}.${extension}`;
            const path = getStoragePath(filename);

            if (!path) {
                throw new Error("Invalid chat type for storage");
            }

            const audioRef = storageRef(storage, path);
            const uploadTask = uploadBytesResumable(audioRef, blob);

            return new Promise((resolve, reject) => {
                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        setUploadProgress(progress);
                    },
                    (error) => {
                        console.error("Upload error:", error);
                        setIsUploading(false);
                        reject(error);
                    },
                    async () => {
                        try {
                            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                            const dbPath = getDatabasePath();

                            if (!dbPath) {
                                throw new Error("Invalid chat type for database");
                            }

                            const messagesRef = ref(database, dbPath);
                            const newMessageRef = push(messagesRef);

                            const messageData = {
                                audioUrl: downloadURL,
                                senderId: currentUser.uid,
                                timestamp: Date.now(),
                            };

                            // Add seen status based on chat type
                            if (chatType === 'private') {
                                messageData.seen = false;
                            } else {
                                messageData.seenBy = { [currentUser.uid]: true };
                            }

                            // For private chats with free/host logic
                            if (chatType === 'private' && chatUser) {
                                // You can add request chat logic here if needed
                            }

                            await set(newMessageRef, messageData);

                            setIsUploading(false);
                            setUploadProgress(0);
                            resolve(downloadURL);
                        } catch (err) {
                            console.error("Error after upload:", err);
                            setIsUploading(false);
                            reject(err);
                        }
                    }
                );
            });
        } catch (err) {
            console.error("uploadAudio() failed:", err);
            setIsUploading(false);
            throw err;
        }
    }, [currentUser, chatId, chatType, chatUser, getStoragePath, getDatabasePath]);

    // Check if platform supports recording
    const isRecordingSupported = useCallback(() => {
        return !!(window.isInApp || (navigator.mediaDevices && navigator.mediaDevices.getUserMedia));
    }, []);

    return {
        // States
        isRecording,
        recordingPopup,
        isUploading,
        uploadProgress,

        // Actions
        startRecording,
        stopRecording,
        cancelRecording,
        uploadAudio,
        isRecordingSupported,

        // For custom implementations
        setRecordingPopup,
        setIsRecording,
        setIsUploading,
        setUploadProgress
    };
};